import React from 'react';
import { Information16 } from '@carbon/icons-react';
import { SCREEN_SIZE } from 'types';
import { SkeletonText, Modal } from 'carbon-components-react';
import ReferralList from '../ReferralList';
import InteractiveMapList, {
  getReferralsWithWebMap
} from '../InteractiveMapList';
import EmptyState from '../EmptyState';
import ExpiredState from '../ExpiredState';
import LookUpAndLiveButton from '../LookUpAndLiveButton';
import LookUpAndLiveContent from '../LookUpAndLiveContent';
import ResponsesContent from '../ResponsesContent';
import PreviewerSuperModal from '../PreviewerSuperModal';
import ResponsesExpiredModal from '../ResponsesExpiredModal';
import OrganisationContactsModal from '../OrganisationContactsModal';
import Downloads from '../Downloads';
import Tools from '../Tools';
import { getISPLink } from 'appConstants/ads';

type Props = {
  referrals: any;
  screenSize: String;
  handleBackButtonClick: Function;
  goToTop: Function;
  enquiryId: number;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
  creatorUserId: number;
  enquiryInformationRef: any;
  isModal: boolean;
  handleCLOOpenModal: any;
  isLoading: boolean;
  timestamp: Date;
  enquiryDetail: any;
  setSelectedReferralExternalId: Function;
  handleContactsSelection: Function;
  shareId: string;
  onClickCloseResponseExpiredModal: Function;
  selectedReferralOverview: number;
  setSelectedReferralOverview: Function;
  setIsDownloadPdf: Function;
  setIsDownloadZip: Function;
  enquiryValidityDate: string;
  lualExtent: any;
  isFromSwxLocate: boolean;

  // Desktop-specific props
  selectedReferral: number;
  selectedReferralObj: any;
  selectedResponse: number;
  showDMFViewer: boolean;
  setShowDMFViewer: Function;
  loadingEmailPreview: any;
  viewOrDownloadDropdownItem: Array<any>;
  handleViewOrDownloadDropdownChange: Function;
  previewData: any;
  handleExpandClick: Function;
  initialSelectedViewOrDownloadItem: any;
  isLoadingPreviewer: boolean;
  handleContactsIconClick: Function;
  getPlanResponsesAboutRef: any;
  inboundEmailIdToDownload: number;
  selectionSource?: 'interactive-map' | 'responses';
  hasSpatialDataLink?: boolean;
  onDownloadSpatialDataClick?: () => void;
  onViewResponseClick?: () => void;
  showPreviewerSuperModal: boolean;
  onClosePreviewerSuperModal: Function;
  showOrganisationContactsModal: boolean;
  showLualContent: boolean;
  handleShowLualContent: () => void;
  handleOpenLual: () => void;
  handleExpandLual: () => void;
  showLualSuperModal: boolean;
  onCloseLualSuperModal: () => void;
  handleResponseSelection: Function;
  handleMultipleResponseSelection: Function;
  handleInteractiveMapSelection: (
    referralIndex: number,
    responseIndex: number
  ) => void;
  allResponsesReceived: boolean;
  copyPageLinkState: any;
  handleRenewEnquiry: Function;
  handleCopyPageLink: Function;
  isOwner: boolean;
  responseReceivedText: string;
  lualZoomLevel?: number | null;
  lualMapZoomLevel?: number | null;
  onLualMapZoomChange?: (zoomLevel: number) => void;
};

const DesktopView: React.FC<Props> = ({
  referrals,
  screenSize,
  enquiryId,
  responsesExpired,
  showResponsesExpiredModal,
  enquiryInformationRef,
  isModal,
  handleCLOOpenModal,
  isLoading,
  timestamp,
  enquiryDetail,
  handleContactsSelection,
  shareId,
  onClickCloseResponseExpiredModal,
  setIsDownloadPdf,
  setIsDownloadZip,
  enquiryValidityDate,
  lualExtent,
  isFromSwxLocate,

  selectedReferral,
  selectedReferralObj,
  selectedResponse,
  showDMFViewer,
  setShowDMFViewer,
  loadingEmailPreview,
  viewOrDownloadDropdownItem,
  handleViewOrDownloadDropdownChange,
  previewData,
  handleExpandClick,
  initialSelectedViewOrDownloadItem,
  isLoadingPreviewer,
  handleContactsIconClick,
  getPlanResponsesAboutRef,
  inboundEmailIdToDownload,
  selectionSource,
  hasSpatialDataLink,
  onDownloadSpatialDataClick,
  onViewResponseClick,
  showPreviewerSuperModal,
  onClosePreviewerSuperModal,
  showOrganisationContactsModal,
  showLualContent,
  handleShowLualContent,
  handleOpenLual,
  handleExpandLual,
  showLualSuperModal,
  onCloseLualSuperModal,
  handleResponseSelection,
  handleMultipleResponseSelection,
  handleInteractiveMapSelection,
  allResponsesReceived,
  copyPageLinkState,
  handleRenewEnquiry,
  handleCopyPageLink,
  isOwner,
  responseReceivedText,
  lualZoomLevel,
  lualMapZoomLevel,
  onLualMapZoomChange
}) => {
  const referralsWithWebMap = React.useMemo(
    () => getReferralsWithWebMap(referrals || []),
    [referrals]
  );

  return (
    <div
      id="getPlans"
      className={`get-plans ${isFromSwxLocate ? 'locate-theme' : ''}`}
    >
      <div className="get-plans-actions">
        {!isFromSwxLocate && (
          <div className="enquiry-validity">
            {!isLoading && (
              <>
                <Information16 />
                {`This enquiry is valid until ${enquiryValidityDate}`}
              </>
            )}
            {isLoading && <SkeletonText />}
          </div>
        )}
        {isFromSwxLocate && referralsWithWebMap.length > 0 && (
          <InteractiveMapList
            referralsWithWebMap={referralsWithWebMap}
            selectedReferral={selectedReferral}
            selectedResponse={selectedResponse}
            handleInteractiveMapSelection={handleInteractiveMapSelection}
            selectionSource={selectionSource}
            responsesExpired={responsesExpired}
            isLoading={isLoading}
          />
        )}
        <ReferralList
          referrals={referrals}
          responseReceivedText={responseReceivedText}
          handleResponseSelection={handleResponseSelection}
          handleMultipleResponseSelection={handleMultipleResponseSelection}
          selectedReferral={selectedReferral}
          selectedResponse={selectedResponse}
          selectionSource={selectionSource}
          isLoading={isLoading}
          timestamp={timestamp}
          enquiryDetail={enquiryDetail}
          responsesExpired={responsesExpired}
          showResponsesExpiredModal={showResponsesExpiredModal}
        />
        {lualExtent && (
          <LookUpAndLiveButton
            responsesExpired={responsesExpired}
            isLoading={isLoading}
            onShowLual={handleShowLualContent}
          />
        )}
        <Downloads
          allResponsesReceived={allResponsesReceived}
          enquiryId={enquiryId}
          shareId={shareId}
          isSmallScreen={screenSize === SCREEN_SIZE.SM}
          responsesExpired={responsesExpired}
          isLoading={isLoading}
          confirmationFileId={enquiryDetail?.confirmationFileId}
          setIsDownloadPdf={setIsDownloadPdf}
          setIsDownloadZip={setIsDownloadZip}
          lualZoomLevel={lualZoomLevel}
          lualMapZoomLevel={lualMapZoomLevel}
        />
        {!isFromSwxLocate && (
          <Tools
            copyPageLinkState={copyPageLinkState}
            renewEnquiryHandler={handleRenewEnquiry}
            copyPageLinkHandler={handleCopyPageLink}
            showCopyPageLink={isOwner && !responsesExpired}
            isSmallScreen={screenSize === SCREEN_SIZE.SM}
            handleCLOOpenModal={handleCLOOpenModal}
            responsesExpired={responsesExpired}
            showResponsesExpiredModal={showResponsesExpiredModal}
            isLoading={isLoading}
          />
        )}
      </div>

      <div className="get-plans-responses">
        {selectedReferral < 0 && !showLualContent && !responsesExpired && (
          <EmptyState isLoading={isLoading} />
        )}
        {selectedReferral >= 0 && !showLualContent && !responsesExpired && (
          <>
            <ResponsesContent
              showDMFViewer={showDMFViewer}
              setShowDMFViewer={setShowDMFViewer}
              isFromSwxLocate={isFromSwxLocate}
              selectedReferral={selectedReferral}
              selectedReferralObj={selectedReferralObj}
              screenSize={screenSize}
              enquiryId={isFromSwxLocate ? enquiryId : undefined}
              loadingEmailPreview={loadingEmailPreview}
              viewOrDownloadDropdownItem={viewOrDownloadDropdownItem}
              handleViewOrDownloadDropdownChange={
                handleViewOrDownloadDropdownChange
              }
              previewData={previewData}
              handleExpandClick={handleExpandClick}
              initialSelectedViewOrDownloadItem={
                initialSelectedViewOrDownloadItem
              }
              isLoadingPreviewer={isLoadingPreviewer}
              handleContactsIconClick={handleContactsIconClick}
              getPlanResponsesAboutRef={getPlanResponsesAboutRef}
              enquiryInformationRef={enquiryInformationRef}
              isModal={isModal}
              handleContactsSelection={handleContactsSelection}
              inboundEmailId={inboundEmailIdToDownload}
              selectionSource={selectionSource}
              hasSpatialDataLink={hasSpatialDataLink}
              onDownloadSpatialDataClick={onDownloadSpatialDataClick}
              onViewResponseClick={onViewResponseClick}
            />
          </>
        )}

        {showResponsesExpiredModal && (
          <div className="get-plans-responses-expired">
            <div className="get-plans-responses-expired-previewer"></div>
            <div className="get-plans-responses-expired-about"></div>
          </div>
        )}
        {responsesExpired && !showResponsesExpiredModal && !showLualContent && (
          <ExpiredState isLoading={isLoading} />
        )}
        {!showResponsesExpiredModal && !showLualContent && !isFromSwxLocate && (
          <div
            className="get-plans-responses-about"
            ref={getPlanResponsesAboutRef}
          >
            <div className="get-plans-responses-about-description">
              <span className="get-plans-responses-about-description-title">
                What you should know about responses and plans.
              </span>
              Plans are indicative only and property connections may not be
              shown. All assets may not be included in the response provided.
              You can engage a skilled locator to assist with the location of
              assets. Check your spam/junk folders for missing responses. Please
              follow safe excavation advice.
            </div>
          </div>
        )}
        {!showResponsesExpiredModal && !showLualContent && !isFromSwxLocate && (
          <div className="get-plans-responses-ads">
            <div className="get-plans-responses-ads-left">
              <img className="logo" src="../iseekplant/logo.png" alt="" />
            </div>
            <div className="get-plans-responses-ads-center">
              <div className="info">
                <div className="info-title">
                  Get FREE quotes for the equipment & contractors you need with
                  iseekplant&apos;s marketplace!
                </div>
                <div className="info-content">
                  <div className="number">1</div>Fill out your job details in
                  our FREE quick quote form.
                </div>
                <div className="info-content">
                  <div className="number">2</div>We send the request to trusted
                  local contractors.
                </div>
                <div className="info-content">
                  <div className="number">3</div>The local contractors will
                  contact you directly with quotes.
                </div>
              </div>
            </div>
            <div className="get-plans-responses-ads-right">
              <button
                onClick={() =>
                  window.open(getISPLink('plan-response-selection'), '_blank')
                }
              >
                <div className="button-content">
                  <div className="icon-container">
                    <img
                      className="icon"
                      src="../iseekplant/icon.jpeg"
                      alt=""
                    />
                  </div>
                  Get FREE quotes
                </div>
              </button>
            </div>
          </div>
        )}

        {showLualContent && (
          <LookUpAndLiveContent
            extent={lualExtent}
            enquiryShape={enquiryDetail?.shape}
            onOpenLual={handleOpenLual}
            onExpandLual={handleExpandLual}
            onLualMapZoomChange={onLualMapZoomChange}
          />
        )}
      </div>

      {showPreviewerSuperModal && (
        <PreviewerSuperModal
          showDMFViewer={showDMFViewer}
          setShowDMFViewer={setShowDMFViewer}
          isOpen={showPreviewerSuperModal}
          onClose={onClosePreviewerSuperModal}
          previewData={previewData}
          selectedReferralObj={selectedReferralObj}
          isModal={isModal}
          inboundEmailId={inboundEmailIdToDownload}
          enquiryId={enquiryId}
        />
      )}
      <ResponsesExpiredModal
        showResponsesExpiredModal={showResponsesExpiredModal && !isLoading}
        onClickClose={onClickCloseResponseExpiredModal}
        isModal={isModal}
      />
      <OrganisationContactsModal
        modalOpen={showOrganisationContactsModal}
        selectedReferralObj={selectedReferralObj}
        handleCloseClick={handleContactsIconClick}
      />
      {showLualSuperModal && (
        <div className="previewer-super-modal">
          <Modal
            open={showLualSuperModal}
            modalHeading="Look up and Live"
            onRequestClose={onCloseLualSuperModal}
            passiveModal
            size="lg"
            className={`lual-super-modal super-modal ${
              isModal ? '' : 'single-modal'
            }`}
          >
            <div className="super-modal-content">
              <div className="super-modal-content-body">
                <LookUpAndLiveContent
                  extent={lualExtent}
                  enquiryShape={enquiryDetail?.shape}
                  onOpenLual={handleOpenLual}
                  onExpandLual={onCloseLualSuperModal}
                  onLualMapZoomChange={onLualMapZoomChange}
                />
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default DesktopView;
